import React, { useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Menu, Divider, Badge, theme } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { isCustom } from '@prerender/billing-shared';

import { useCharbeePlanId, useHasTrackingCode, useIsPrerenderUser, useUserRole } from '../../hooks/usePrerenderUser';
import useFeatureFlag, { FeatureFlag } from '../../hooks/useFeatureFlag';
import allMenuItems, { PageKey, MenuItem, LeafMenuItem } from './menuItems';
import { UserRole } from '../../features/PrerenderUser';
import { useSetupChecklist } from '~features/SetupChecklist';
import { useHasSearchPerformanceAccess } from '~features/SearchPerformance';
import { useHasAiInsightsAccess } from '~features/ai-insights';

type ParsedMenuItem = Pick<MenuItem, 'type' | 'label' | 'extra'> & {
  key: string;
  icon?: React.ReactNode;
  children?: ParsedMenuItem[];
  order: number;
};

function filterVisibleMenuItem(
  item: MenuItem,
  visibilityCheckerFn: (item: LeafMenuItem) => boolean
): MenuItem | undefined {
  if (item.type === 'group') {
    const children = item.children.filter((child) => visibilityCheckerFn(child));

    if (children.length) {
      return { ...item, children };
    }
  } else if (visibilityCheckerFn(item)) {
    return item;
  }
}

type LinksMenuProps = {
  isCollapsed: boolean;
};
const LinksMenu = ({ isCollapsed }: LinksMenuProps) => {
  const { token } = theme.useToken();
  const location = useLocation();
  const oversightEnabled = useFeatureFlag([FeatureFlag.Oversight]);
  const customDealEnabled = useFeatureFlag([FeatureFlag.CustomDeal]);
  const hasTrackingCode = useHasTrackingCode();
  const isPrerenderUser = useIsPrerenderUser();
  const canUseSearchPerformance = useHasSearchPerformanceAccess();
  const canUseAiInsights = useHasAiInsightsAccess();
  const chargebeePlanId = useCharbeePlanId();
  const role = useUserRole();
  // setup checklist stuff
  const { remainingItemsCount } = useSetupChecklist();

  const shouldBeVisible = useCallback(
    (item: LeafMenuItem) => {
      switch (item.id) {
        case PageKey.IntegrationWizard:
          return role !== UserRole.Guest;

        case PageKey.DomainAnalytics:
          return isPrerenderUser;

        case PageKey.AIInsights:
          return canUseAiInsights;

        case PageKey.SearchPerformance:
          return canUseSearchPerformance;

        case PageKey.Oversight:
          return oversightEnabled;

        case PageKey.CustomDeal:
          return isCustom(chargebeePlanId) || customDealEnabled;

        default:
          return item.visible;
      }
    },
    [
      hasTrackingCode,
      isPrerenderUser,
      canUseSearchPerformance,
      canUseAiInsights,
      chargebeePlanId,
      role,
      customDealEnabled,
      oversightEnabled,
    ]
  );

  const decoreItem = (item: LeafMenuItem) => {
    if (item.id === PageKey.IntegrationWizard && hasTrackingCode) {
      return {
        ...item,
        extra: <Badge count={remainingItemsCount} styles={{ indicator: { backgroundColor: token.colorPrimary } }} />,
        order: remainingItemsCount > 0 ? item.order : 12.5,
        icon: remainingItemsCount > 0 ? item.icon : <CheckOutlined style={{ color: token.colorTextSecondary }} />,
        label:
          remainingItemsCount > 0 ? item.label : <span style={{ color: token.colorTextSecondary }}>{item.label}</span>,
      };
    }
    return item;
  };

  const visibleMenuItems = React.useMemo(
    () =>
      allMenuItems
        .map((item) => filterVisibleMenuItem(item, shouldBeVisible))
        .filter((item): item is MenuItem => !!item),
    [allMenuItems, shouldBeVisible]
  );

  const itemPathToItemKey: Record<string, string> = React.useMemo(() => {
    return visibleMenuItems.reduce<Record<string, MenuItem['id']>>((acc, item) => {
      if (item.type === 'group') {
        item.children.forEach((child) => (acc[child.target] = child.id));
      } else {
        acc[item.target] = item.id;
      }
      return acc;
    }, {});
  }, [visibleMenuItems]);

  const menuItems: ParsedMenuItem[] = useMemo(
    () =>
      visibleMenuItems
        .reduce((acc, item) => {
          if (item.type === 'group') {
            acc.push({
              type: 'group',
              key: item.id,
              label: isCollapsed ? <Divider style={{ margin: '10px 0' }} /> : item.label,
              children: item.children
                .sort((a, b) => a.order - b.order)
                .map((child) => ({
                  key: child.id,
                  label: child.label,
                  icon: child.icon,
                  type: 'item',
                  order: child.order,
                })),
              order: item.order,
            });
          } else if (item.type === 'item') {
            const decoratedItem = decoreItem(item);
            acc.push({
              key: decoratedItem.id,
              label: decoratedItem.label,
              icon: decoratedItem.icon,
              type: 'item',
              extra: decoratedItem.extra,
              order: decoratedItem.order,
            });
          }
          return acc;
        }, [] as ParsedMenuItem[])
        .sort((a, b) => a.order - b.order),
    [visibleMenuItems, shouldBeVisible, isCollapsed, remainingItemsCount, hasTrackingCode]
  );

  const selectedKey = itemPathToItemKey[`/${location.pathname.split('/')[1]}`];

  return <Menu selectedKeys={[selectedKey]} mode="inline" items={menuItems} inlineIndent={12} />;
};

export default React.memo(LinksMenu);
